/**
 * Calendario comercial de calle (tiendas, bares, restaurantes y servicios).
 * Base fija sin IA: campañas, rotaciones de oferta, compras y festivos.
 * Los textos viven en translations.ts (sección retailCalEvents: `<key>_t` / `<key>_r`).
 * Funciones puras: se usan en el navegador y en el servidor (misión semanal).
 */

export type CalCategory = "sales" | "offer" | "purchases" | "legal";
export type CalSource = "system" | "ai_suggestion" | "user_custom";
export type CalStatus = "active" | "inactive" | "suggested";
export type BusinessKind = "retail" | "hospitality";
type Scope = "all" | BusinessKind;

export const CAL_CATEGORIES: CalCategory[] = ["sales", "offer", "purchases", "legal"];
/** Tonos de las fechas propias (tokens --cal-* en styles.css). */
export const CAL_COLORS = ["red", "orange", "gold", "green", "blue", "violet", "teal", "graphite"] as const;
/** Tonos de la paleta anterior: se traducen al tono de prioridad más cercano. */
const LEGACY_COLORS: Record<string, CalColor> = { clay: "red", terracotta: "red", ochre: "orange", mustard: "gold", sage: "green", olive: "green", moss: "green", slate: "blue", stone: "blue", plum: "violet", petrol: "teal" };
export function toCalColor(c: string | null | undefined): CalColor | null {
  if (!c) return null;
  if ((CAL_COLORS as readonly string[]).includes(c)) return c as CalColor;
  return LEGACY_COLORS[c] ?? null;
}
/** Nombres que el usuario pone a cada tono (user_preferences.calendar_color_labels). */
export type CalColorLabels = Partial<Record<CalColor, string>>;
export function cleanColorLabels(raw: unknown): CalColorLabels {
  const out: CalColorLabels = {};
  if (!raw || typeof raw !== "object") return out;
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    const c = toCalColor(k);
    if (c && typeof v === "string" && v.trim() && (!out[c] || (CAL_COLORS as readonly string[]).includes(k))) out[c] = v.trim().slice(0, 40);
  }
  return out;
}
export type CalColor = (typeof CAL_COLORS)[number];

interface BaseDef {
  key: string;
  category: CalCategory;
  scope: Scope;
  lead: number;
  /** Fecha (y fin opcional) para un año dado. */
  at: (y: number) => { start: Date; end?: Date };
  /** Solo en estas comunidades (normalizadas). */
  regions?: string[];
}

const d = (y: number, m: number, day: number) => new Date(Date.UTC(y, m - 1, day));
const fixed = (m: number, day: number) => (y: number) => ({ start: d(y, m, day) });

/** Domingo de Pascua (algoritmo de Meeus/Butcher). */
export function easterSunday(y: number): Date {
  const a = y % 19, b = Math.floor(y / 100), c = y % 100, dd = Math.floor(b / 4), e = b % 4;
  const f = Math.floor((b + 8) / 25), g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - dd - g + 15) % 30, i = Math.floor(c / 4), k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7, m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31), day = ((h + l - 7 * m + 114) % 31) + 1;
  return d(y, month, day);
}
const addDays = (dt: Date, n: number) => new Date(dt.getTime() + n * 86400000);

/** Viernes siguiente al cuarto jueves de noviembre. */
export function blackFriday(y: number): Date {
  const first = d(y, 11, 1);
  const firstThu = 1 + ((4 - first.getUTCDay() + 7) % 7);
  return d(y, 11, firstThu + 21 + 1);
}
/** Primer domingo de mayo (Día de la Madre en España). */
export function mothersDay(y: number): Date {
  const first = d(y, 5, 1);
  return d(y, 5, 1 + ((7 - first.getUTCDay()) % 7));
}

export function normRegion(r: string | null | undefined): string {
  return (r ?? "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z]/g, "");
}

export function businessKindFor(sector: string | null | undefined): BusinessKind {
  const s = (sector ?? "").toLowerCase();
  return /bar|restaur|hostel|cafe|cafeter|gastro|taberna|cocteler/.test(s) ? "hospitality" : "retail";
}

export const BASE_EVENTS: BaseDef[] = [
  { key: "reyes_eve", category: "sales", scope: "all", lead: 30, at: fixed(1, 5) },
  { key: "reyes", category: "legal", scope: "all", lead: 14, at: fixed(1, 6) },
  { key: "new_year", category: "legal", scope: "all", lead: 10, at: fixed(1, 1) },
  { key: "winter_sales", category: "sales", scope: "retail", lead: 21, at: fixed(1, 7) },
  { key: "sales_transition", category: "offer", scope: "retail", lead: 14, at: fixed(2, 15) },
  { key: "valentine", category: "sales", scope: "all", lead: 21, at: fixed(2, 14) },
  { key: "andalucia_day", category: "legal", scope: "all", lead: 10, at: fixed(2, 28), regions: ["andalucia"] },
  { key: "spring_collection", category: "offer", scope: "retail", lead: 21, at: fixed(3, 1) },
  { key: "terrace_permit", category: "purchases", scope: "hospitality", lead: 30, at: fixed(3, 1) },
  { key: "fathers_day", category: "sales", scope: "all", lead: 21, at: fixed(3, 19) },
  { key: "easter_prep", category: "offer", scope: "all", lead: 21, at: (y) => ({ start: addDays(easterSunday(y), -10) }) },
  { key: "good_friday", category: "legal", scope: "all", lead: 14, at: (y) => ({ start: addDays(easterSunday(y), -2) }) },
  { key: "easter_monday", category: "legal", scope: "all", lead: 14, at: (y) => ({ start: addDays(easterSunday(y), 1) }), regions: ["cataluna", "comunidadvalenciana", "valencia", "paisvasco", "navarra", "illesbalears", "baleares"] },
  { key: "labour_day", category: "legal", scope: "all", lead: 10, at: fixed(5, 1) },
  { key: "madrid_day", category: "legal", scope: "all", lead: 10, at: fixed(5, 2), regions: ["madrid", "comunidaddemadrid"] },
  { key: "mothers_day", category: "sales", scope: "all", lead: 21, at: (y) => ({ start: mothersDay(y) }) },
  { key: "summer_menu", category: "offer", scope: "hospitality", lead: 21, at: fixed(5, 15) },
  { key: "summer_plan", category: "offer", scope: "all", lead: 30, at: fixed(6, 1) },
  { key: "sant_joan", category: "legal", scope: "all", lead: 14, at: fixed(6, 24), regions: ["cataluna"] },
  { key: "summer_sales", category: "sales", scope: "retail", lead: 21, at: fixed(7, 1) },
  { key: "santiago", category: "legal", scope: "all", lead: 10, at: fixed(7, 25), regions: ["galicia"] },
  { key: "august_valley", category: "offer", scope: "all", lead: 21, at: fixed(8, 1) },
  { key: "assumption", category: "legal", scope: "all", lead: 10, at: fixed(8, 15) },
  { key: "back_to_school", category: "sales", scope: "retail", lead: 30, at: fixed(9, 1) },
  { key: "diada", category: "legal", scope: "all", lead: 10, at: fixed(9, 11), regions: ["cataluna"] },
  { key: "autumn_menu", category: "offer", scope: "hospitality", lead: 21, at: fixed(10, 1) },
  { key: "winter_plan", category: "offer", scope: "all", lead: 30, at: fixed(10, 1) },
  { key: "valencia_day", category: "legal", scope: "all", lead: 10, at: fixed(10, 9), regions: ["comunidadvalenciana", "valencia"] },
  { key: "hispanic_day", category: "legal", scope: "all", lead: 10, at: fixed(10, 12) },
  { key: "xmas_packaging", category: "purchases", scope: "retail", lead: 21, at: fixed(10, 20) },
  { key: "xmas_supplies", category: "purchases", scope: "hospitality", lead: 21, at: fixed(11, 10) },
  { key: "all_saints", category: "legal", scope: "all", lead: 10, at: fixed(11, 1) },
  { key: "xmas_lights", category: "offer", scope: "all", lead: 21, at: fixed(11, 20) },
  { key: "black_friday", category: "sales", scope: "all", lead: 30, at: (y) => ({ start: blackFriday(y), end: addDays(blackFriday(y), 3) }) },
  { key: "company_dinners", category: "sales", scope: "hospitality", lead: 45, at: (y) => ({ start: d(y, 12, 1), end: d(y, 12, 22) }) },
  { key: "xmas_campaign", category: "sales", scope: "retail", lead: 30, at: (y) => ({ start: d(y, 12, 1), end: d(y, 12, 24) }) },
  { key: "constitution", category: "legal", scope: "all", lead: 14, at: fixed(12, 6) },
  { key: "immaculate", category: "legal", scope: "all", lead: 14, at: fixed(12, 8) },
  { key: "christmas", category: "legal", scope: "all", lead: 14, at: fixed(12, 25) },
  { key: "sant_esteve", category: "legal", scope: "all", lead: 14, at: fixed(12, 26), regions: ["cataluna", "illesbalears", "baleares"] },
  { key: "new_year_eve", category: "purchases", scope: "hospitality", lead: 21, at: fixed(12, 31) },
  { key: "inventory", category: "purchases", scope: "all", lead: 14, at: fixed(12, 30) },
];

export interface CalendarItem {
  id: string;
  /** Clave del evento base (null en fechas propias o de Sara IA). */
  systemKey: string | null;
  start: string; // YYYY-MM-DD
  end: string | null;
  category: CalCategory;
  source: CalSource;
  status: CalStatus;
  lead: number;
  /** Texto libre (fechas propias / Sara IA); en fechas base viene de translations. */
  title: string | null;
  reason: string | null;
  notes: string | null;
  rowId: string | null;
  /** Color elegido por el usuario (solo fechas propias). */
  color: CalColor | null;
}

export interface CalendarRow {
  id: string;
  system_key: string | null;
  date_start: string;
  date_end: string | null;
  title: string;
  category: string;
  reason: string | null;
  notes: string | null;
  source: string;
  status: string;
  lead_days: number;
  color?: string | null;
}

const iso = (dt: Date) => dt.toISOString().slice(0, 10);

/** Ocurrencias base en la ventana [from, from+days] para el tipo de negocio y comunidad. */
export function baseOccurrences(from: Date, days: number, kind: BusinessKind, region: string | null | undefined): CalendarItem[] {
  const r = normRegion(region);
  const startMs = Date.UTC(from.getUTCFullYear(), from.getUTCMonth(), from.getUTCDate());
  const endMs = startMs + days * 86400000;
  const out: CalendarItem[] = [];
  for (const y of [from.getUTCFullYear() - 1, from.getUTCFullYear(), from.getUTCFullYear() + 1]) {
    for (const ev of BASE_EVENTS) {
      if (ev.scope !== "all" && ev.scope !== kind) continue;
      if (ev.regions && !ev.regions.includes(r)) continue;
      const { start, end } = ev.at(y);
      const last = (end ?? start).getTime();
      if (last < startMs || start.getTime() > endMs) continue;
      const k = `${ev.key}@${y}`;
      out.push({ id: `sys:${k}`, systemKey: k, start: iso(start), end: end ? iso(end) : null, category: ev.category, source: "system", status: "active", lead: ev.lead, title: null, reason: null, notes: null, rowId: null, color: null });
    }
  }
  return out;
}

const asColor = toCalColor;
const asCat = (c: string): CalCategory => ((CAL_CATEGORIES as string[]).includes(c) ? (c as CalCategory) : "sales");

/** Une la base con las filas del usuario (estado/nota de fechas base + fechas propias y de Sara IA). */
export function mergeCalendar(base: CalendarItem[], rows: CalendarRow[], from: Date, days: number): CalendarItem[] {
  const bySys = new Map(rows.filter((r) => r.system_key).map((r) => [r.system_key as string, r]));
  const merged = base.map((b) => {
    const r = b.systemKey ? bySys.get(b.systemKey) : undefined;
    return r ? { ...b, status: (r.status as CalStatus) ?? b.status, notes: r.notes, rowId: r.id, lead: r.lead_days ?? b.lead } : b;
  });
  const startIso = iso(from);
  const endIso = iso(new Date(from.getTime() + days * 86400000));
  for (const r of rows) {
    const isGrant = !!r.system_key?.startsWith("grant:");
    if ((r.system_key && !isGrant) || r.title === "-") continue;
    const last = r.date_end ?? r.date_start;
    if (last < startIso || r.date_start > endIso) continue;
    merged.push({ id: r.id, systemKey: isGrant ? r.system_key : null, start: r.date_start, end: r.date_end, category: asCat(r.category), source: (r.source as CalSource) ?? "user_custom", status: (r.status as CalStatus) ?? "active", lead: r.lead_days, title: r.title, reason: r.reason, notes: r.notes, rowId: r.id, color: asColor(r.color) });
  }
  return merged.sort((a, b) => a.start.localeCompare(b.start));
}

/** Días que faltan (0 = hoy; negativo si ya empezó y sigue en curso). */
export function daysUntil(startIso: string, today: Date): number {
  const t = Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate());
  return Math.round((Date.parse(`${startIso}T00:00:00Z`) - t) / 86400000);
}

/** Fechas activas en los próximos `days` días, ordenadas. */
export function upcomingActive(items: CalendarItem[], today: Date, days = 30): CalendarItem[] {
  return items.filter((i) => i.status === "active" && daysUntil(i.end ?? i.start, today) >= 0 && daysUntil(i.start, today) <= days);
}

/** Fechas que ya están dentro de su ventana de preparación (Sara las prioriza). */
export function inPrepWindow(items: CalendarItem[], today: Date): CalendarItem[] {
  return items.filter((i) => i.status === "active" && daysUntil(i.end ?? i.start, today) >= 0 && daysUntil(i.start, today) <= i.lead);
}
